// src/components/Activity/VideosExplicativos.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';

function VideosExplicativos({ temas }) {
  const { id } = useParams();
  const tema = temas.find((t) => t.id === parseInt(id));

  if (!tema) {
    return (
      <div className="text-red-500">
        Tema no encontrado.{' '}
        <Link to="/" className="text-blue-600 hover:underline">
          Volver al Inicio
        </Link>
      </div>
    );
  }

  // Los vídeos ahora pueden venir en `tema.videos`.
  // Si no existe, mantenemos compatibilidad y buscamos en `tema.contenidos` los ids que empiecen por 'video'.
  const videos = Array.isArray(tema.videos) && tema.videos.length > 0
    ? tema.videos
    : (tema.contenidos || []).filter((c) => c && c.id && c.id.startsWith('video'));

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button 
          onClick={() => window.history.back()}
          className="w-full sm:w-auto border border-teal-600 text-teal-600 hover:bg-teal-50 transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center justify-center rounded-md font-medium h-9 px-4 py-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </button>
      </div>
      <h2 className="text-2xl font-bold text-teal-700 mb-6">Vídeos Explicativos</h2>
      {videos.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {videos.map((video) => (
            <div key={video.id} className="rounded-xl border bg-white shadow hover:shadow-lg transition-shadow duration-300 p-4 sm:p-6 flex flex-col items-center">
              <h3 className="text-base sm:text-lg font-semibold text-teal-700 mb-2 text-center truncate">{video.titulo}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{video.descripcion}</p>
              <div className="w-full aspect-video mb-4">
                {(() => {
                  const isVideoFile = typeof video.url === 'string' && video.url.match(/\.(mp4|webm|ogg)$/i);
                  const isLocalPath = typeof video.url === 'string' && video.url.startsWith('/');

                  if (isVideoFile || isLocalPath) {
                    return (
                      <video
                        src={video.url}
                        controls
                        className="w-full h-full border-0 rounded-lg"
                        title={video.titulo}
                      />
                    );
                  }

                  return (
                    <iframe
                      src={video.url}
                      title={video.titulo}
                      className="w-full h-full border-0 rounded-lg"
                      allowFullScreen
                    />
                  );
                })()}
              </div>
              <button
                onClick={() => window.open(video.url, '_blank')}
                className="w-full inline-flex items-center justify-center rounded-md font-medium transition-colors bg-teal-500 hover:bg-teal-600 text-white h-10 px-4 py-2"
              >
                <ExternalLink className="mr-2 h-4 w-4" /> Abrir en Nueva Pestaña
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-teal-600">No hay vídeos explicativos disponibles para este tema.</p>
      )}
      <div className="flex justify-end mt-6">
        <button 
          onClick={() => window.history.back()}
          className="w-full sm:w-auto border border-teal-600 text-teal-600 hover:bg-teal-50 transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center justify-center rounded-md font-medium h-9 px-4 py-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </button>
      </div>
    </div>
  );
}

VideosExplicativos.propTypes = {
  temas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      titulo: PropTypes.string.isRequired,
      descripcion: PropTypes.string.isRequired,
      contenidos: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          titulo: PropTypes.string,
          descripcion: PropTypes.string,
          url: PropTypes.string
        })
      ),
      videos: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          titulo: PropTypes.string.isRequired,
          descripcion: PropTypes.string,
          url: PropTypes.string.isRequired
        })
      )
    })
  ).isRequired
};

export default VideosExplicativos;
